import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trophy, Award, Users, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../lib/auth";
import { usePublicConfig } from "../lib/publicConfig";

const BOARDS = [
  { value: "dealers", label: "Dealers", subtitle: "Automotive crews", icon: Trophy },
  { value: "operators", label: "Operators", subtitle: "Founders & business teams", icon: Users },
];

function rankBadge(rank) {
  if (rank === 1) return { text: "1st", cls: "bg-amber-400 text-fc-navy" };
  if (rank === 2) return { text: "2nd", cls: "bg-white/80 text-fc-navy" };
  if (rank === 3) return { text: "3rd", cls: "bg-[#B97D2A] text-white" };
  return null;
}

export default function Leaderboard() {
  const { user } = useAuth();
  const cfg = usePublicConfig();
  const [params, setParams] = useSearchParams();
  const isAdmin = user?.role === "super_admin";
  const defaultBoard = (() => {
    const aud = (user?.audience || "automotive").toLowerCase();
    if (aud === "business" || aud === "entrepreneur") return "operators";
    return "dealers";
  })();
  const [board, setBoard] = useState(params.get("board") || defaultBoard);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Phase 17 — kill-switch gate. Skip network + render empty state for non-admins.
  const isDisabled = !cfg.leaderboards_enabled && !isAdmin;

  useEffect(() => {
    if (isDisabled) return;
    setParams((sp) => { const n = new URLSearchParams(sp); n.set("board", board); return n; });
    setLoading(true); setErr("");
    api.get(`/leaderboard/by-audience?board=${board}&limit=50`)
      .then((r) => setData(r.data))
      .catch((e) => { console.error("leaderboard load failed", e); setErr("Couldn't load leaderboard."); setData(null); })
      .finally(() => setLoading(false));
  }, [board, setParams, isDisabled]);

  if (isDisabled) {
    return (
      <div className="flex bg-fc-light-gray min-h-screen">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center p-10" data-testid="leaderboard-disabled">
          <div className="max-w-md text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-fc-red/10 items-center justify-center mb-5">
              <Trophy className="text-fc-red" size={22} />
            </div>
            <h1 className="text-2xl font-black text-fc-navy mb-3">Rankings are coming soon.</h1>
            <p className="text-fc-medium-gray text-sm leading-relaxed mb-6">
              We&apos;re finishing up the leaderboard experience. Your XP is still being tracked in the background — you&apos;ll see your position the moment we flip this on.
            </p>
            <Link to="/dashboard" className="btn-fc-red inline-flex items-center gap-2" data-testid="leaderboard-disabled-back">
              Back to dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Non-admin viewer can peek at the other board but doesn't appear in it.
  const isOnOwnBoard = isAdmin || board === defaultBoard;

  return (
    <div className="min-h-screen bg-fc-navy flex" data-surface="light" data-testid="leaderboard-page">
      <Sidebar />
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        <div className="h-20 px-6 lg:px-10 flex items-center justify-between border-b border-gray-200 bg-white/70 backdrop-blur sticky top-0 z-30">
          <div>
            <p className="fc-overline text-[10px]">LEADERBOARD</p>
            <h1 className="text-lg sm:text-xl font-black tracking-tight">Anonymized rankings</h1>
            <p className="text-[10px] tracking-[0.18em] uppercase text-gray-400">
              Real names stay private. Only your city or initials appear.
            </p>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-8 space-y-8">
          {/* Tab toggle */}
          <div className="flex flex-wrap gap-2" data-testid="board-tabs">
            {BOARDS.map((b) => {
              const Icon = b.icon;
              const active = board === b.value;
              return (
                <button
                  key={b.value}
                  onClick={() => setBoard(b.value)}
                  className={`px-4 py-2 text-xs tracking-[0.18em] font-bold uppercase border rounded inline-flex items-center gap-2 transition-colors ${
                    active ? "border-fc-red bg-fc-red text-white"
                           : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid={`board-${b.value}`}
                >
                  <Icon size={14} /> {b.label}
                </button>
              );
            })}
            {!isOnOwnBoard && (
              <span className="self-center ml-2 text-[10px] tracking-[0.18em] uppercase text-amber-300/80">
                Peeking · you don't appear on this board
              </span>
            )}
          </div>

          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : err ? (
            <p className="text-gray-500">{err}</p>
          ) : !data ? null : (
            <>
              {/* Top 3 hero strip */}
              <section data-testid="lb-top3">
                <h2 className="text-[10px] tracking-[0.22em] uppercase font-black text-amber-300/85 mb-3 inline-flex items-center gap-2">
                  <Trophy size={12} /> Top 3
                </h2>
                {data.top3.length === 0 ? (
                  <p className="text-gray-500 text-sm">No one's on the board yet. Be the first.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {data.top3.map((row) => {
                      const badge = rankBadge(row.rank);
                      return (
                        <div key={row.rank} className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-4" data-testid={`top3-${row.rank}`}>
                          <span className={`inline-flex w-10 h-10 rounded-full items-center justify-center text-[10px] font-black tracking-wide ${badge.cls}`}>
                            {badge.text}
                          </span>
                          <div className="min-w-0">
                            <p className="font-mono font-bold truncate text-fc-red">{row.display_name}</p>
                            <p className="text-[11px] text-gray-500">{row.xp.toLocaleString()} XP · {row.badges_count} badges</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Top 5% stat strip */}
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="lb-stats">
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-gray-500 font-bold mb-1 inline-flex items-center gap-1.5">
                    <Sparkles size={11} className="text-amber-300" /> Top 5% threshold
                  </p>
                  <p className="text-2xl font-black tracking-tight">{(data.top_5_percent?.threshold || 0).toLocaleString()}+ <span className="text-xs font-bold text-gray-500 tracking-wide">XP</span></p>
                  <p className="text-[10px] text-gray-400 mt-1">{data.top_5_percent?.count || 0} members at or above</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-gray-500 font-bold mb-1 inline-flex items-center gap-1.5">
                    <Users size={11} className="text-blue-300" /> Total participants
                  </p>
                  <p className="text-2xl font-black tracking-tight">{(data.total_participants || 0).toLocaleString()}</p>
                </div>
                {data.me && (
                  <div className="bg-fc-red/10 border border-fc-red/40 rounded-2xl p-5" data-testid="lb-my-standing">
                    <p className="text-[10px] tracking-[0.18em] uppercase text-fc-red font-bold mb-1 inline-flex items-center gap-1.5">
                      <Award size={11} /> Your standing
                    </p>
                    <p className="text-2xl font-black tracking-tight">
                      #{data.me.rank}
                      <span className="ml-2 text-xs font-bold text-gray-500 tracking-wide">· {data.me.xp.toLocaleString()} XP</span>
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1 font-mono">{data.me.display_name}</p>
                  </div>
                )}
              </section>

              {/* Full table */}
              <section data-testid="lb-table">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] tracking-[0.18em] uppercase text-gray-500 bg-gray-50">
                      <tr>
                        <th className="text-left p-3 w-16">Rank</th>
                        <th className="text-left p-3">Display name</th>
                        <th className="text-right p-3">XP</th>
                        <th className="text-right p-3 hidden sm:table-cell">Courses</th>
                        <th className="text-right p-3 hidden sm:table-cell">Badges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.leaderboard.map((row) => (
                        <tr key={row.rank}
                            className={`border-t border-gray-100 ${row.is_me ? "bg-amber-400/10" : ""}`}
                            data-testid={`lb-row-${row.rank}`}>
                          <td className="p-3 font-bold">
                            {row.rank <= 3 && <Trophy size={13} className={`inline mr-1 ${row.rank===1?"text-[#F59E0B]":row.rank===2?"text-[#9CA3AF]":"text-[#B97D2A]"}`} />}
                            {row.rank}
                            {row.is_me && (
                              <span className="ml-2 text-[9px] tracking-[0.18em] uppercase font-black bg-fc-red text-white px-1.5 py-0.5 rounded">YOU</span>
                            )}
                          </td>
                          <td className="p-3 font-mono text-fc-red font-bold">{row.display_name}</td>
                          <td className="p-3 text-right font-black text-base">{row.xp.toLocaleString()}</td>
                          <td className="p-3 text-right text-gray-500 hidden sm:table-cell">{row.courses_completed}</td>
                          <td className="p-3 text-right text-gray-500 hidden sm:table-cell">{row.badges_count}</td>
                        </tr>
                      ))}
                      {data.leaderboard.length === 0 && (
                        <tr><td colSpan={5} className="p-6 text-center text-gray-500">
                          No one's on the board yet. Be the first.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-gray-400 mt-3 tracking-wide">
                  Want a friendlier display name? Add your city &amp; state under <Link to="/settings" className="underline hover:text-gray-900">Settings → Profile</Link>.
                </p>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
